import React, { useState, useEffect } from 'react';
import { Pie, Doughnut, Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
} from 'chart.js';
import {
  FaChartPie,
  FaBlog,
  FaProjectDiagram,
  FaCommentAlt,
  FaUsers,
  FaArrowUp,
  FaArrowDown,
  FaCalendar,
  FaEye,
  FaDatabase,
  FaChartBar,
  FaChartLine
} from 'react-icons/fa';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
);

const DataBaseDashboard = () => {
  const [blogCount, setBlogCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [testimonialCount, setTestimonialCount] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('pie');
  const [timeRange, setTimeRange] = useState('weekly');
  const [contentChartView, setContentChartView] = useState('pie'); // 'pie' or 'histogram'
  const [statusChartView, setStatusChartView] = useState('pie'); // 'pie' or 'histogram'
  
  const [metrics, setMetrics] = useState({
    totals: {
      totalBlogs: 0,
      totalProjects: 0,
      totalTestimonials: 0,
      totalViews: 0,
      activeUsers: 0
    },
    blogStatus: { active: 0, inactive: 0 },
    projectStatus: { ongoing: 0, completed: 0 },
    testimonialStatus: { live: 0, inactive: 0 },
    contentDistribution: { blogs: 0, projects: 0, testimonials: 0 },
    recentActivity: [],
  });

  // Chart data for blog status (active vs inactive)
  const blogStatusData = {
    labels: ['Active Blogs', 'Inactive Blogs'],
    datasets: [
      {
        data: [metrics.blogStatus.active, metrics.blogStatus.inactive],
        backgroundColor: ['#5DC350', '#E36324'],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  // Histogram data for blog status
  const blogStatusHistogramData = {
    labels: ['Active Blogs', 'Inactive Blogs'],
    datasets: [
      {
        label: 'Count',
        data: [metrics.blogStatus.active, metrics.blogStatus.inactive],
        backgroundColor: ['#5DC350', '#E36324'],
        borderColor: ['#4CAF50', '#FF5722'],
        borderWidth: 2,
        borderRadius: 8,
        barPercentage: 0.6,
      },
    ],
  };

  // Chart data for content distribution
  const contentDistributionData = {
    labels: ['Blogs', 'Projects', 'Testimonials'],
    datasets: [
      {
        data: [
          metrics.contentDistribution.blogs,
          metrics.contentDistribution.projects,
          metrics.contentDistribution.testimonials,
        ],
        backgroundColor: ['#5DC350', '#E36324', '#4C6FFF'],
        borderWidth: 0,
        hoverOffset: 15,
      },
    ],
  };

  // Histogram data for content distribution
  const contentDistributionHistogramData = {
    labels: ['Blogs', 'Projects', 'Testimonials'],
    datasets: [
      {
        label: 'Count',
        data: [
          metrics.contentDistribution.blogs,
          metrics.contentDistribution.projects,
          metrics.contentDistribution.testimonials,
        ],
        backgroundColor: ['#5DC350', '#E36324', '#4C6FFF'],
        borderColor: ['#4CAF50', '#FF5722', '#3B5BFF'],
        borderWidth: 2,
        borderRadius: 8,
        barPercentage: 0.6,
      },
    ],
  };

  // Combined status data for comparison chart
  const combinedStatusData = {
    labels: ['Active Blogs', 'Inactive Blogs', 'Ongoing Projects', 'Completed Projects', 'Live Testimonials', 'Inactive Testimonials'],
    datasets: [
      {
        label: 'Count',
        data: [
          metrics.blogStatus.active,
          metrics.blogStatus.inactive,
          metrics.projectStatus.ongoing,
          metrics.projectStatus.completed,
          metrics.testimonialStatus.live,
          metrics.testimonialStatus.inactive,
        ],
        backgroundColor: [
          '#5DC350', // Active Blogs
          '#E36324', // Inactive Blogs
          '#4C6FFF', // Ongoing Projects
          '#FFC107', // Completed Projects
          '#9C27B0', // Live Testimonials
          '#607D8B'  // Inactive Testimonials
        ],
        borderColor: [
          '#4CAF50',
          '#FF5722',
          '#3B5BFF',
          '#FFA000',
          '#7B1FA2',
          '#455A64'
        ],
        borderWidth: 2,
        borderRadius: 8,
        barPercentage: 0.6,
      },
    ],
  };

  // Line chart for trend analysis
  const trendAnalysisData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Blogs',
        data: [15, 18, 16, 21, 19, 21],
        borderColor: '#5DC350',
        backgroundColor: 'rgba(93, 195, 80, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Projects',
        data: [4, 5, 5, 6, 6, 7],
        borderColor: '#E36324',
        backgroundColor: 'rgba(227, 99, 36, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Testimonials',
        data: [8, 9, 10, 11, 11, 12],
        borderColor: '#4C6FFF',
        backgroundColor: 'rgba(76, 111, 255, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Blog performance data - dynamically adjust based on time range
  const getBlogPerformanceData = () => {
    const baseData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          label: 'Blog Views',
          data: [12000, 19000, 15000, 25000, 22000, 30000],
          backgroundColor: '#5DC350',
          borderColor: '#4CAF50',
          borderWidth: 2,
          borderRadius: 8,
        },
        {
          label: 'Blog Engagement',
          data: [8000, 12000, 10000, 18000, 15000, 22000],
          backgroundColor: '#E36324',
          borderColor: '#FF5722',
          borderWidth: 2,
          borderRadius: 8,
        }
      ],
    };

    // Adjust data based on time range
    if (timeRange === 'weekly') {
      return {
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [
          {
            ...baseData.datasets[0],
            data: [4500, 5200, 4800, 6100, 5800, 4900, 5300],
          },
          {
            ...baseData.datasets[1],
            data: [3200, 3800, 3500, 4200, 4000, 3500, 3700],
          }
        ],
      };
    } else if (timeRange === 'monthly') {
      return baseData;
    } else { // yearly
      return {
        labels: ['Q1', 'Q2', 'Q3', 'Q4'],
        datasets: [
          {
            ...baseData.datasets[0],
            data: [46000, 67000, 52000, 78000],
          },
          {
            ...baseData.datasets[1],
            data: [30000, 43000, 35000, 52000],
          }
        ],
      };
    }
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          usePointStyle: true,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          usePointStyle: true,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
  };

  // Recent activity data
  const activities = metrics.recentActivity.map((a, i) => ({
    id: a.id || i,
    type: a.type,
    title: a.title,
    time: new Date(a.time).toLocaleString(),
    status: a.status,
  }));

  // Quick stats data - updated with API data
  const quickStats = [
    {
      id: 1,
      title: 'Total Views',
      value: totalViews.toLocaleString(),
      change: '+4%',
      isPositive: true,
      icon: FaEye,
      color: '#5DC350'
    },
    {
      id: 2,
      title: 'Active Users',
      value: activeUsers.toLocaleString(),
      change: '+8%',
      isPositive: true,
      icon: FaUsers,
      color: '#4C6FFF'
    },
    {
      id: 3,
      title: 'Database Records',
      value: (blogCount + projectCount + testimonialCount).toLocaleString(),
      change: '+12%',
      isPositive: true,
      icon: FaDatabase,
      color: '#E36324'
    },
  ];

  // Count up animation function
  const animateCount = (setter, target, duration = 600) => {
    let start = 0;
    const increment = target / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setter(target);
        clearInterval(timer);
      } else {
        setter(Math.floor(start));
      }
    }, 16);
  };

  // Fetch data from API
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true);
        console.log('Fetching dashboard data...');
        const response = await fetch('https://mithahomesfrontend.pemixcels.com/api/MaincardApi/dashboard/counts');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API Response:', data);
        
        if (data.success) {
          const apiData = data.data;
          
          // Animate the counts from API
          animateCount(setBlogCount, apiData.blogs || 0);
          animateCount(setProjectCount, apiData.projects || 0);
          animateCount(setTestimonialCount, apiData.testimonials || 0);
          animateCount(setTotalViews, apiData.totalViews || 0);
          animateCount(setActiveUsers, apiData.activeUsers || 0);
          
          // Update metrics with API data
          setMetrics(prev => ({
            ...prev,
            totals: {
              totalBlogs: apiData.blogs || 0,
              totalProjects: apiData.projects || 0,
              totalTestimonials: apiData.testimonials || 0,
              totalViews: apiData.totalViews || 0,
              activeUsers: apiData.activeUsers || 0
            },
            contentDistribution: {
              blogs: apiData.blogs || 0,
              projects: apiData.projects || 0,
              testimonials: apiData.testimonials || 0
            }
          }));
          
          // Calculate status percentages based on API data
          if (apiData.blogs > 0) {
            setMetrics(prev => ({
              ...prev,
              blogStatus: {
                active: Math.floor(apiData.blogs * 0.7),
                inactive: Math.floor(apiData.blogs * 0.3)
              }
            }));
          }
          
          if (apiData.projects > 0) {
            setMetrics(prev => ({
              ...prev,
              projectStatus: {
                ongoing: Math.floor(apiData.projects * 0.4),
                completed: Math.floor(apiData.projects * 0.6)
              }
            }));
          }
          
          if (apiData.testimonials > 0) {
            setMetrics(prev => ({
              ...prev,
              testimonialStatus: {
                live: Math.floor(apiData.testimonials * 0.8),
                inactive: Math.floor(apiData.testimonials * 0.2)
              }
            }));
          }
          
          // Generate recent activity based on API data
          const recentActivities = [];
          if (apiData.blogs > 0) {
            recentActivities.push({
              id: 1,
              type: 'blog',
              title: 'New blog post published',
              time: new Date().toISOString(),
              status: 'published'
            });
          }
          if (apiData.projects > 0) {
            recentActivities.push({
              id: 2,
              type: 'project',
              title: 'Project milestone completed',
              time: new Date().toISOString(),
              status: 'completed'
            });
          }
          if (apiData.testimonials > 0) {
            recentActivities.push({
              id: 3,
              type: 'testimonial',
              title: 'New testimonial added',
              time: new Date().toISOString(),
              status: 'new'
            });
          }
          
          setMetrics(prev => ({
            ...prev,
            recentActivity: recentActivities
          }));
          
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (error) {
        console.error('Error fetching dashboard counts:', error);
        setError('Failed to load dashboard data. Please check if the API server is running.');
        
        // Fallback to demo data matching your API response
        animateCount(setBlogCount, 21);
        animateCount(setProjectCount, 7);
        animateCount(setTestimonialCount, 12);
        animateCount(setTotalViews, 4);
        animateCount(setActiveUsers, 4);
        
        // Update metrics with fallback data
        setMetrics({
          totals: {
            totalBlogs: 21,
            totalProjects: 7,
            totalTestimonials: 12,
            totalViews: 4,
            activeUsers: 4
          },
          blogStatus: { active: 15, inactive: 6 },
          projectStatus: { ongoing: 3, completed: 4 },
          testimonialStatus: { live: 10, inactive: 2 },
          contentDistribution: { blogs: 21, projects: 7, testimonials: 12 },
          recentActivity: [
            {
              id: 1,
              type: 'blog',
              title: 'Sample Blog Post Published',
              time: new Date().toISOString(),
              status: 'published'
            },
            {
              id: 2,
              type: 'project',
              title: 'Sample Project Updated',
              time: new Date().toISOString(),
              status: 'updated'
            },
            {
              id: 3,
              type: 'testimonial',
              title: 'New Testimonial Added',
              time: new Date().toISOString(),
              status: 'new'
            }
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  // Get activity status class
  const getActivityStatusClass = (status) => {
    switch (status) {
      case 'published':
        return 'status-published';
      case 'completed':
        return 'status-completed';
      case 'new':
        return 'status-new';
      case 'updated':
        return 'status-updated';
      case 'draft':
        return 'status-drafted';
      case 'Completed':
        return 'status-Completed';
      case 'Ongoing':
        return 'status-Ongoing';
      case 'Ready to Occupy':
        return 'status-Ready to Occupy';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Loading Dashboard Data...</h3>
        </div>
        <style jsx>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: 100vh;
            font-size: 18px;
            color: var(--dark-color);
          }
          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 5px solid #f3f3f3;
            border-top: 5px solid var(--primary-color);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return <></>;
  }

  return (
    <div className="dashboard-container">
      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h2>Dashboard Overview</h2>
          <div className="header-actions">
            <button className="btn btn-primary">
              <FaCalendar className="btn-icon" />
              Last 30 Days
            </button>
            <button className="btn btn-secondary">
              Export Report
            </button>
          </div>
        </div>

        {/* Quick Stats - Updated with API Data */}
        <div className="quick-stats">
          {quickStats.map((stat) => (
            <div key={stat.id} className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                <stat.icon />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-title">{stat.title}</div>
                <div className={`stat-change ${stat.isPositive ? 'positive' : 'negative'}`}>
                  {stat.isPositive ? <FaArrowUp /> : <FaArrowDown />}
                  {stat.change}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Cards Grid - Updated with API Data */}
        <div className="cards-grid">
          <div className="card card-blog">
            <div className="card-header">
              <div>
                <div className="card-title">Total Blogs</div>
                <div className="card-value count-animation">{blogCount}</div>
              </div>
              <div className="card-icon">
                <FaBlog />
              </div>
            </div>
            <div className="card-footer">
              <span className="footer-text">
                {metrics.blogStatus?.active ?? 0} Active • {metrics.blogStatus?.inactive ?? 0} Inactive
              </span>
            </div>
          </div>

          <div className="card card-project">
            <div className="card-header">
              <div>
                <div className="card-title">Total Projects</div>
                <div className="card-value count-animation">{projectCount}</div>
              </div>
              <div className="card-icon">
                <FaProjectDiagram />
              </div>
            </div>
            <div className="card-footer">
              <span className="footer-text">
                {metrics.projectStatus?.ongoing ?? 0} Ongoing • {metrics.projectStatus?.completed ?? 0} Completed
              </span>
            </div>
          </div>

          <div className="card card-testimonial">
            <div className="card-header">
              <div>
                <div className="card-title">Testimonials</div>
                <div className="card-value count-animation">{testimonialCount}</div>
              </div>
              <div className="card-icon">
                <FaCommentAlt />
              </div>
            </div>
            <div className="card-footer">
              <span className="footer-text">
                {metrics.testimonialStatus?.live ?? 0} Published • {metrics.testimonialStatus?.inactive ?? 0} Draft
              </span>
            </div>
          </div>

          <div className="card card-user">
            <div className="card-header">
              <div>
                <div className="card-title">Total Views</div>
                <div className="card-value count-animation">{totalViews}</div>
              </div>
              <div className="card-icon">
                <FaEye />
              </div>
            </div>
            <div className="card-footer">
              <span className="footer-text">
                Based on total website views
              </span>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-section">
          {/* Blog Performance Chart */}
          <div className="chart-container full-width">
            <div className="chart-header">
              <div className="chart-title">Blog Performance</div>
              <div className="chart-actions">
                <button
                  className={timeRange === 'weekly' ? 'active' : ''}
                  onClick={() => setTimeRange('weekly')}
                >
                  Weekly
                </button>
                <button
                  className={timeRange === 'monthly' ? 'active' : ''}
                  onClick={() => setTimeRange('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={timeRange === 'yearly' ? 'active' : ''}
                  onClick={() => setTimeRange('yearly')}
                >
                  Yearly
                </button>
              </div>
            </div>
            <div className="chart-wrapper">
              <Bar data={getBlogPerformanceData()} options={barChartOptions} />
            </div>
          </div>

          {/* Content Distribution Chart */}
          <div className="chart-container">
            <div className="chart-header">
              <div className="chart-title">Content Distribution</div>
              <div className="chart-actions">
                <button
                  className={contentChartView === 'pie' ? 'active' : ''}
                  onClick={() => setContentChartView('pie')}
                  title="Pie Chart View"
                >
                  <FaChartPie />
                </button>
                <button
                  className={contentChartView === 'histogram' ? 'active' : ''}
                  onClick={() => setContentChartView('histogram')}
                  title="Histogram View"
                >
                  <FaChartBar />
                </button>
              </div>
            </div>
            <div className="chart-wrapper">
              {contentChartView === 'pie' ? (
                <Pie data={contentDistributionData} options={chartOptions} />
              ) : (
                <Bar data={contentDistributionHistogramData} options={barChartOptions} />
              )}
            </div>
            <div className="chart-summary">
              <div className="summary-item">
                <span className="summary-label">Blogs:</span>
                <span className="summary-value">{blogCount}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Projects:</span>
                <span className="summary-value">{projectCount}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Testimonials:</span>
                <span className="summary-value">{testimonialCount}</span>
              </div>
            </div>
          </div>

          {/* Blog Status Chart */}
          <div className="chart-container">
            <div className="chart-header">
              <div className="chart-title">Blog Status Distribution</div>
              <div className="chart-actions">
                <button
                  className={statusChartView === 'pie' ? 'active' : ''}
                  onClick={() => setStatusChartView('pie')}
                  title="Pie Chart View"
                >
                  <FaChartPie />
                </button>
                <button
                  className={statusChartView === 'histogram' ? 'active' : ''}
                  onClick={() => setStatusChartView('histogram')}
                  title="Histogram View"
                >
                  <FaChartBar />
                </button>
              </div>
            </div>
            <div className="chart-wrapper">
              {statusChartView === 'pie' ? (
                <Pie data={blogStatusData} options={chartOptions} />
              ) : (
                <Bar data={blogStatusHistogramData} options={barChartOptions} />
              )}
            </div>
            <div className="chart-summary">
              <div className="summary-item">
                <span className="summary-label">Active:</span>
                <span className="summary-value">{metrics.blogStatus.active}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Inactive:</span>
                <span className="summary-value">{metrics.blogStatus.inactive}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total:</span>
                <span className="summary-value">{blogCount}</span>
              </div>
            </div>
          </div>

          {/* Combined Status Chart */}
          <div className="chart-container full-width">
            <div className="chart-header">
              <div className="chart-title">All Content Status Comparison</div>
              <div className="chart-actions">
                <button className="active" disabled>
                  <FaChartBar /> Histogram View
                </button>
              </div>
            </div>
            <div className="chart-wrapper">
              <Bar data={combinedStatusData} options={barChartOptions} />
            </div>
          </div>

          {/* Trend Analysis Chart */}
          <div className="chart-container full-width">
            <div className="chart-header">
              <div className="chart-title">Content Growth Trends</div>
              <div className="chart-actions">
                <button className="active" disabled>
                  <FaChartLine /> Line Chart View
                </button>
              </div>
            </div>
            <div className="chart-wrapper">
              <Line data={trendAnalysisData} options={lineChartOptions} />
            </div>
          </div>
        </div>

        {/* Data Summary Cards */}
        <div className="data-summary">
          <div className="summary-card">
            <div className="summary-header">
              <FaDatabase className="summary-icon" />
              <h3>Content Totals</h3>
            </div>
            <div className="summary-content">
              <div className="summary-row">
                <span>Total Blogs:</span>
                <strong>{blogCount}</strong>
              </div>
              <div className="summary-row">
                <span>Total Projects:</span>
                <strong>{projectCount}</strong>
              </div>
              <div className="summary-row">
                <span>Total Testimonials:</span>
                <strong>{testimonialCount}</strong>
              </div>
              <div className="summary-row total">
                <span>Overall Total:</span>
                <strong>{blogCount + projectCount + testimonialCount}</strong>
              </div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-header">
              <FaChartBar className="summary-icon" />
              <h3>Status Breakdown</h3>
            </div>
            <div className="summary-content">
              <div className="status-item">
                <div className="status-color" style={{ backgroundColor: '#5DC350' }}></div>
                <span>Active Blogs:</span>
                <strong>{metrics.blogStatus.active}</strong>
              </div>
              <div className="status-item">
                <div className="status-color" style={{ backgroundColor: '#E36324' }}></div>
                <span>Inactive Blogs:</span>
                <strong>{metrics.blogStatus.inactive}</strong>
              </div>
              <div className="status-item">
                <div className="status-color" style={{ backgroundColor: '#4C6FFF' }}></div>
                <span>Ongoing Projects:</span>
                <strong>{metrics.projectStatus.ongoing}</strong>
              </div>
              <div className="status-item">
                <div className="status-color" style={{ backgroundColor: '#FFC107' }}></div>
                <span>Completed Projects:</span>
                <strong>{metrics.projectStatus.completed}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        :root {
          --primary-color: #5DC350;
          --secondary-color: #E36324;
          --dark-color: #2D3748;
          --light-color: #F7FAFC;
          --gray-color: #A0AEC0;
          --card-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          --sidebar-width: 250px;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        body {
          background-color: #F0F2F5;
          color: var(--dark-color);
          line-height: 1.6;
        }

        .dashboard-container {
          min-height: 100vh;
          background-color: #f8fafc;
        }

        /* Main Content */
        .main-content {
          padding: 30px;
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .content-header h2 {
          font-size: 28px;
          font-weight: 600;
          color: var(--dark-color);
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: var(--primary-color);
          color: white;
        }

        .btn-secondary {
          background: white;
          color: var(--dark-color);
          border: 1px solid var(--gray-color);
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        /* Quick Stats */
        .quick-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: var(--card-shadow);
          display: flex;
          align-items: center;
          gap: 15px;
          transition: transform 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
        }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .stat-title {
          font-size: 14px;
          color: var(--gray-color);
          margin-bottom: 4px;
        }

        .stat-change {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .stat-change.positive {
          color: var(--primary-color);
        }

        .stat-change.negative {
          color: #FF6B6B;
        }

        /* Cards Grid */
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: var(--card-shadow);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }

        .card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 5px;
          height: 100%;
        }

        .card-blog::before {
          background-color: var(--primary-color);
        }

        .card-project::before {
          background-color: var(--secondary-color);
        }

        .card-testimonial::before {
          background-color: #4C6FFF;
        }

        .card-user::before {
          background-color: #FF6B6B;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
        }

        .card-icon {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: white;
        }

        .card-blog .card-icon {
          background-color: var(--primary-color);
        }

        .card-project .card-icon {
          background-color: var(--secondary-color);
        }

        .card-testimonial .card-icon {
          background-color: #4C6FFF;
        }

        .card-user .card-icon {
          background-color: #FF6B6B;
        }

        .card-title {
          font-size: 14px;
          color: var(--gray-color);
          margin-bottom: 8px;
        }

        .card-value {
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .card-change {
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .card-change.positive {
          color: var(--primary-color);
        }

        .card-change.negative {
          color: #FF6B6B;
        }

        .card-footer {
          border-top: 1px solid #eee;
          padding-top: 15px;
          margin-top: 15px;
        }

        .footer-text {
          font-size: 12px;
          color: var(--gray-color);
        }

        /* Charts Section */
        .charts-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .chart-container {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: var(--card-shadow);
          margin-bottom: 20px;
        }

        .chart-container.full-width {
          grid-column: 1 / -1;
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .chart-title {
          font-size: 18px;
          font-weight: 600;
        }

        .chart-actions {
          display: flex;
          gap: 8px;
        }

        .chart-actions button {
          background: none;
          border: none;
          color: var(--gray-color);
          cursor: pointer;
          font-size: 14px;
          padding: 6px 12px;
          border-radius: 6px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .chart-actions button:not(:disabled):hover {
          background-color: rgba(93, 195, 80, 0.1);
          color: var(--primary-color);
        }

        .chart-actions button.active {
          color: var(--primary-color);
          font-weight: 600;
          background-color: rgba(93, 195, 80, 0.1);
        }

        .chart-actions button:disabled {
          opacity: 0.7;
          cursor: default;
        }

        .chart-wrapper {
          position: relative;
          height: 300px;
          margin-bottom: 15px;
        }

        .chart-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          padding: 15px;
          background: #f8fafc;
          border-radius: 8px;
          border-top: 2px solid #eee;
        }

        .summary-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px;
        }

        .summary-label {
          font-size: 12px;
          color: var(--gray-color);
          margin-bottom: 4px;
        }

        .summary-value {
          font-size: 18px;
          font-weight: 700;
          color: var(--dark-color);
        }

        /* Data Summary Cards */
        .data-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 20px;
          margin-top: 30px;
        }

        .summary-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: var(--card-shadow);
        }

        .summary-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f0f0f0;
        }

        .summary-icon {
          font-size: 24px;
          color: var(--primary-color);
        }

        .summary-header h3 {
          font-size: 20px;
          font-weight: 600;
          color: var(--dark-color);
        }

        .summary-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: #f8fafc;
          border-radius: 8px;
        }

        .summary-row.total {
          background: rgba(93, 195, 80, 0.1);
          border: 1px solid var(--primary-color);
          font-weight: 600;
        }

        .status-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px;
          background: #f8fafc;
          border-radius: 8px;
        }

        .status-color {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }

        .status-item span {
          flex: 1;
        }

        .status-item strong {
          font-size: 16px;
        }

        /* Animation for count up */
        @keyframes countUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .count-animation {
          animation: countUp 1s ease-out;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .charts-section {
            grid-template-columns: 1fr;
          }
          
          .chart-container.full-width {
            grid-column: 1;
          }
        }

        @media (max-width: 992px) {
          .charts-section {
            grid-template-columns: 1fr;
          }
          
          .data-summary {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 20px;
          }

          .content-header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }

          .header-actions {
            width: 100%;
            justify-content: flex-start;
          }

          .quick-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .cards-grid {
            grid-template-columns: 1fr;
          }

          .chart-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .chart-actions {
            align-self: stretch;
            justify-content: center;
          }

          .chart-summary {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 576px) {
          .quick-stats {
            grid-template-columns: 1fr;
          }

          .stat-card {
            padding: 15px;
          }

          .card {
            padding: 20px;
          }

          .chart-container {
            padding: 20px;
          }

          .chart-wrapper {
            height: 250px;
          }

          .chart-summary {
            grid-template-columns: 1fr;
          }

          .summary-card {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default DataBaseDashboard;